import fp from 'lodash/fp';
import moment from 'moment-timezone';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { triangle, position } from 'polished';
import { wrapDisplayName, withHandlers } from 'recompose';
import { Manager, Target, Popper, Arrow as PopperArrow } from 'react-popper';
import onClickOutside from 'react-onclickoutside';
import { getLayer, color } from '@deerplusofficial/pos-helper';
import { ButtonBase, Icon } from '@deerplusofficial/pos-components';
import { Calendar, Time } from '../components';

const arrowDimensions = {
  height: 6,
  width: 18,
};

const Datepicker = fp.compose(
  withHandlers({
    handleClickOutside: ({ onClickOutside }) => ({ target }) =>
      onClickOutside({ target }),
  }),
  onClickOutside
)(styled.div`
  width: 270px;
  padding: 24px;
  background-color: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  border-radius: 5px;
`);

const Tabs = styled.div`
  display: flex;
  align-items: center;
  padding-top: 16px;
`;

const Tab = styled(ButtonBase)`
  flex-grow: 1;
  text-align: center;
  color: ${props => (props.active ? color.text.info : color.text.secondary)};
`;

const Arrow = styled.div`
  overflow: hidden;
  &::before {
    content: '';
    filter: blur(1px);
    transform: translate(-50%);
    ${triangle({
      pointingDirection: 'top',
      height: arrowDimensions.height + 2,
      width: arrowDimensions.width + 2,
      foregroundColor: 'rgba(0, 0, 0, .1)',
    })};
    ${position(
      'absolute',
      `-${arrowDimensions.height + 2}px`,
      null,
      null,
      '50%'
    )};
  }
  &::after {
    content: '';
    transform: translate(-50%);
    ${triangle({
      pointingDirection: 'top',
      height: arrowDimensions.height + 4,
      width: arrowDimensions.width + 4,
      foregroundColor: '#ffffff',
    })};
    ${position(
      'absolute',
      `-${arrowDimensions.height + 2}px`,
      null,
      null,
      '50%'
    )};
  }
`;

export default () => Component =>
  class extends React.Component {
    static displayName = wrapDisplayName(Component, 'WithDatepicker');

    static propTypes = {
      moment: PropTypes.shape(),
      startDate: PropTypes.shape(),
      endDate: PropTypes.shape(),
      onMomentChange: PropTypes.func,
    };

    static defaultProps = {
      moment: moment(),
      startDate: {},
      endDate: moment()
        .hour(23)
        .minute(59),
      onMomentChange: fp.identity,
    };

    state = {
      activeTab: 'date',
      isOpen: false,
    };

    onFocus = () => this.setState({ isOpen: true });

    onClickOutside = ({ target }) => {
      if (target.id === this.props.id) return;

      this.props.onBlur();

      this.setState({ isOpen: false });
    };

    onDateActive = () => this.setState({ activeTab: 'date' });

    onTimeActive = () => this.setState({ activeTab: 'time' });

    render() {
      const { isOpen, activeTab } = this.state;
      const {
        className,
        moment,
        startDate,
        endDate,
        onMomentChange,
        onBlur,
        ...props
      } = this.props;

      return (
        <Manager tag={false}>
          <Target className={className}>
            <Component
              readOnly
              value={moment.format('MM/DD/YYYY HH:mm')}
              onFocus={this.onFocus}
              {...props}
            />
          </Target>
          {isOpen && (
            <Popper placement="bottom">
              {({ popperProps: { style, ...props }, restProps }) => (
                <div
                  {...props}
                  style={{ ...style, zIndex: getLayer('date-picker') }}
                >
                  <Datepicker onClickOutside={this.onClickOutside}>
                    <Calendar
                      hide={activeTab !== 'date'}
                      moment={moment}
                      startDate={startDate}
                      endDate={endDate}
                      today={moment.date()}
                      onMomentChange={onMomentChange}
                    />
                    <Time
                      hide={activeTab !== 'time'}
                      moment={moment}
                      onMomentChange={onMomentChange}
                    />
                    <Tabs>
                      <Tab
                        onClick={this.onDateActive}
                        active={activeTab === 'date'}
                      >
                        <Icon icon="calendar-alt" />
                      </Tab>
                      <Tab
                        onClick={this.onTimeActive}
                        active={activeTab === 'time'}
                      >
                        <Icon icon="clock" />
                      </Tab>
                    </Tabs>
                  </Datepicker>
                  <PopperArrow>
                    {({ arrowProps, restProps }) => (
                      <span {...arrowProps}>
                        <Arrow />
                      </span>
                    )}
                  </PopperArrow>
                </div>
              )}
            </Popper>
          )}
        </Manager>
      );
    }
  };



// WEBPACK FOOTER //
// ./src/assist/withDatepicker.js